import { cn } from "cn";
import type { ReactNode } from "react";
import { Button } from "../button/button";
import { CloseButton } from "../button/close-button";
import type { ToastAction, ToastColorScheme } from "./types";

interface ToastContentProps {
  icon: ReactNode;
  title: string;
  description?: string;
  closable: boolean;
  action?: ToastAction;
  colorScheme?: ToastColorScheme;
  onDismiss: () => void;
}

const schemeClasses: Record<
  ToastColorScheme,
  {
    container: string;
    title: string;
    description: string;
    closeColor: "light" | "dark";
    action: string;
  }
> = {
  dark: {
    // The design system's toast surface is neutral-800 (Figma `Sonner`), a
    // raw scale step with no semantic token in the light theme.
    container: "bg-bg-dark border-border-light-min shadow-lg",
    title: "text-text-light",
    description: "text-text-light-high",
    closeColor: "light",
    // A filled pill: an outline alone read as plain text, so people missed "Undo".
    action: "bg-bg-light text-text-dark-max hover:bg-bg-light/90",
  },
  light: {
    container: "bg-bg-light border-border-dark-low shadow-lg",
    title: "text-text-dark-max",
    description: "text-text-dark-high",
    closeColor: "dark",
    action: "bg-bg-dark text-text-light hover:bg-bg-dark/90",
  },
};

export function ToastContent({
  icon,
  title,
  description,
  closable,
  action,
  colorScheme = "dark",
  onDismiss,
}: ToastContentProps) {
  const scheme = schemeClasses[colorScheme];

  return (
    <div
      className={cn(
        /* sorted */
        "flex items-start gap-3",
        "w-96",
        "p-3",
        "border rounded-3xl",
        scheme.container,
      )}
    >
      <div className="relative flex items-center gap-3 flex-1">
        {icon}
        <div className="flex flex-col gap-1 flex-1">
          <p className={cn("text-sm font-medium", scheme.title)}>{title}</p>
          {description && (
            <p className={cn("text-sm", scheme.description)}>{description}</p>
          )}
          {action && (
            <Button
              size="xs"
              onClick={action.onClick}
              className={cn("mt-1 self-start", scheme.action)}
            >
              {action.label}
            </Button>
          )}
        </div>
      </div>
      {closable && (
        <CloseButton
          size="xs"
          colorScheme={scheme.closeColor}
          onClick={onDismiss}
        />
      )}
    </div>
  );
}
